/**
 * Cookie Consent Management Utility
 * GDPR-compliant cookie consent tracking
 */

const CONSENT_KEY = 'eq_cookie_consent';
const CONSENT_VERSION = '1.0';

/**
 * Cookie categories
 */
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
};

/**
 * Default consent state (only necessary cookies allowed by default)
 */
const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  timestamp: null,
  necessary: true, // Always true, cannot be disabled
  functional: false,
  analytics: false,
  marketing: false,
};

/**
 * Get current consent preferences
 */
export function getConsentPreferences() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored);
    
    // Check if consent version matches
    if (consent.version !== CONSENT_VERSION) {
      return null; // Require new consent for new version
    }

    return consent;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
}

/**
 * Check if user has made a consent choice
 */
export function hasConsent() {
  return getConsentPreferences() !== null;
}

/**
 * Save consent preferences
 */
export function saveConsentPreferences(preferences) {
  const consent = {
    ...DEFAULT_CONSENT,
    timestamp: new Date().toISOString(),
    functional: preferences.functional ?? DEFAULT_CONSENT.functional,
    analytics: preferences.analytics ?? DEFAULT_CONSENT.analytics,
    marketing: preferences.marketing ?? DEFAULT_CONSENT.marketing,
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    console.log('✅ Cookie consent saved:', consent);
    
    // Trigger event for components listening to consent changes
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
    
    return consent;
  } catch (error) {
    console.error('Error saving cookie consent:', error);
    return null;
  }
}

/**
 * Accept all cookies
 */
export function acceptAllCookies() {
  return saveConsentPreferences({
    functional: true,
    analytics: true,
    marketing: true,
  });
}

/**
 * Reject non-essential cookies
 */
export function rejectNonEssentialCookies() {
  return saveConsentPreferences({
    functional: false,
    analytics: false,
    marketing: false,
  });
}

/**
 * Check if a specific cookie category is allowed
 */
export function isCategoryAllowed(category) {
  const consent = getConsentPreferences();
  
  // If no consent given, only necessary cookies allowed
  if (!consent) {
    return category === COOKIE_CATEGORIES.NECESSARY;
  }

  switch (category) {
    case COOKIE_CATEGORIES.NECESSARY:
      return true; // Always allowed
    case COOKIE_CATEGORIES.FUNCTIONAL:
      return consent.functional;
    case COOKIE_CATEGORIES.ANALYTICS:
      return consent.analytics;
    case COOKIE_CATEGORIES.MARKETING:
      return consent.marketing;
    default:
      return false;
  }
}

/**
 * Clear all consent preferences (for testing or user request)
 */
export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
    console.log('🧹 Cookie consent cleared');
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: null }));
  } catch (error) {
    console.error('Error clearing cookie consent:', error);
  }
}

/**
 * Conditional LocalStorage write (respects consent)
 */
export function setStorageItem(key, value, category = COOKIE_CATEGORIES.FUNCTIONAL) {
  if (!isCategoryAllowed(category)) {
    console.warn(`LocalStorage write blocked: ${key} (consent not given for ${category})`);
    return false;
  }

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error('Error writing to localStorage:', error);
    return false;
  }
}

/**
 * Get storage item (always allowed for reading)
 */
export function getStorageItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
}

/**
 * Initialize analytics (only if consent given)
 */
export function initializeAnalytics() {
  if (!isCategoryAllowed(COOKIE_CATEGORIES.ANALYTICS)) {
    console.log('📊 Analytics blocked (no consent)');
    return;
  }

  console.log('📊 Analytics enabled (consent given)');
  // TODO: Initialize Google Analytics or other analytics here
  // Example:
  // window.gtag('config', 'GA_MEASUREMENT_ID');
}

/**
 * Initialize marketing tools (only if consent given)
 */
export function initializeMarketing() {
  if (!isCategoryAllowed(COOKIE_CATEGORIES.MARKETING)) {
    console.log('📢 Marketing blocked (no consent)');
    return;
  }

  console.log('📢 Marketing enabled (consent given)');
  // TODO: Initialize marketing pixels, retargeting, etc.
}

/**
 * Block non-consented scripts
 */
export function blockNonConsentedScripts() {
  const consent = getConsentPreferences();
  
  // If no consent, block analytics and marketing scripts
  if (!consent || !consent.analytics) {
    // Block analytics scripts
    const analyticsScripts = document.querySelectorAll('script[data-category="analytics"]');
    analyticsScripts.forEach(script => {
      script.type = 'text/plain';
      script.setAttribute('data-blocked', 'true');
    });
  }

  if (!consent || !consent.marketing) {
    // Block marketing scripts
    const marketingScripts = document.querySelectorAll('script[data-category="marketing"]');
    marketingScripts.forEach(script => {
      script.type = 'text/plain';
      script.setAttribute('data-blocked', 'true');
    });
  }
}

/**
 * Load consented scripts
 */
export function loadConsentedScripts() {
  const consent = getConsentPreferences();
  
  if (consent?.analytics) {
    // Load analytics scripts
    const analyticsScripts = document.querySelectorAll('script[data-category="analytics"][data-blocked="true"]');
    analyticsScripts.forEach(script => {
      script.type = 'text/javascript';
      script.removeAttribute('data-blocked');
    });
    initializeAnalytics();
  }

  if (consent?.marketing) {
    // Load marketing scripts
    const marketingScripts = document.querySelectorAll('script[data-category="marketing"][data-blocked="true"]');
    marketingScripts.forEach(script => {
      script.type = 'text/javascript';
      script.removeAttribute('data-blocked');
    });
    initializeMarketing();
  }
}
